import { useCallback, useId, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
  RadioGroup,
  RadioGroupItem,
  toast,
} from "@coval/ui"
import { Check, ClipboardCheck } from "lucide-react"

import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"

const log = logger("AddToHumanReviewProject")

function isRateLimitError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  const maybeData =
    typeof error === "object" && error !== null && "data" in error
      ? (error as { data?: { httpStatus?: number; code?: string } }).data
      : undefined

  return (
    error.message.includes("Too Many Requests") ||
    error.message.includes("429") ||
    maybeData?.httpStatus === 429 ||
    maybeData?.code === "TOO_MANY_REQUESTS"
  )
}

interface AddToHumanReviewProjectDropdownProps {
  itemType: "run" | "simulation" | "uploaded conversation"
  selectedKeys: Set<string>
  currentData:
    | ConversationsTableResult[]
    | RunsTableResult[]
    | SimulationsTableResult[]
  humanReviewProjects: HumanReviewProject[]
  isProjectsLoading: boolean
  isProjectsError: boolean
  onRetryProjects: () => void
  fetchHumanReviewProjectWithSimulationIds: (
    projectId: string,
  ) => Promise<HumanReviewProject>
  updateHumanReviewProject: (request: {
    id: string
    simulationOutputs: string[]
  }) => Promise<unknown>
  sendProjectUpdate: (request: {
    projectId: string
    isNewProject?: boolean
    simulationOutputId?: string | null
  }) => Promise<unknown>
  fetchSimulationOutputIdsForRun?: (runId: string) => Promise<string[]>
  isDisabled?: boolean
  onSuccess?: () => void
}

export const AddToHumanReviewProjectDropdown = ({
  itemType,
  selectedKeys,
  currentData,
  humanReviewProjects,
  isProjectsLoading,
  isProjectsError,
  onRetryProjects,
  fetchHumanReviewProjectWithSimulationIds,
  updateHumanReviewProject,
  sendProjectUpdate,
  fetchSimulationOutputIdsForRun,
  isDisabled = false,
  onSuccess,
}: AddToHumanReviewProjectDropdownProps) => {
  const queryParams = useQueryParams()
  const { query } = useRouter()
  const organizationSlug =
    typeof query.slug === "string" ? query.slug : undefined
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string>()
  const projectListLabelId = useId()
  const selectedCount = selectedKeys.size
  const selectedItemLabel = `${itemType}${selectedCount === 1 ? "" : "s"}`
  const capitalizedSelectedItemLabel =
    selectedItemLabel.charAt(0).toUpperCase() + selectedItemLabel.slice(1)

  const handleAddToProject = useCallback(
    async (projectId: string) => {
      if (selectedKeys.size === 0) {
        toast.danger({
          title: "No items selected",
          description: "Please select items to add to project.",
        })
        return
      }

      setIsSaving(true)

      try {
        const currentProject =
          await fetchHumanReviewProjectWithSimulationIds(projectId)

        const isRunMode = itemType === "run"

        let validSimulationOutputIds: string[]

        if (isRunMode) {
          if (!fetchSimulationOutputIdsForRun) {
            throw new Error("Run simulation output lookup is unavailable")
          }

          // For runs: fetch simulation output IDs from selected run IDs
          const selectedIds = Array.from(selectedKeys)

          // Batch requests to avoid rate limiting
          // Process in batches of 10 with 100ms delay between batches
          const BATCH_SIZE = 10
          const BATCH_DELAY_MS = 100
          const simulationOutputResults: string[][] = []

          for (let i = 0; i < selectedIds.length; i += BATCH_SIZE) {
            const batch = selectedIds.slice(i, i + BATCH_SIZE)
            const batchPromises = batch.map((runId) =>
              fetchSimulationOutputIdsForRun(runId),
            )

            try {
              const batchResults = await Promise.all(batchPromises)
              simulationOutputResults.push(...batchResults)
            } catch (error) {
              // If we hit rate limits, wait longer and retry with smaller batches
              // Check for rate limit errors in various formats
              if (isRateLimitError(error)) {
                // Wait 1 second before retrying with individual requests
                await new Promise((resolve) => {
                  setTimeout(resolve, 1000)
                })
                // Retry each run individually with delays to avoid rate limits
                for (const runId of batch) {
                  try {
                    const result = await fetchSimulationOutputIdsForRun(runId)
                    simulationOutputResults.push(result)
                    // Small delay between individual requests after rate limit
                    await new Promise((resolve) => {
                      setTimeout(resolve, 200)
                    })
                  } catch (retryError) {
                    log.error(
                      "Failed to fetch simulation outputs for run",
                      retryError instanceof Error ? retryError : undefined,
                      { runId },
                    )
                    // Continue with other runs even if one fails
                  }
                }
              } else {
                throw error
              }
            }

            // Add delay between batches (except for the last batch)
            if (i + BATCH_SIZE < selectedIds.length) {
              await new Promise((resolve) => {
                setTimeout(resolve, BATCH_DELAY_MS)
              })
            }
          }

          // Extract all simulation output IDs
          validSimulationOutputIds = simulationOutputResults
            .flatMap((results) => results || [])
            .filter((id): id is string => Boolean(id))
        } else {
          // Simulations and uploaded conversations already expose their output IDs.
          const selectedIds = Array.from(selectedKeys)
          const simulationOutputData = currentData as Array<{
            simulation_output_id: string | null
          }>
          validSimulationOutputIds = selectedIds.filter(
            (id) =>
              id &&
              simulationOutputData.some(
                (item) => item.simulation_output_id === id,
              ),
          )
        }

        if (validSimulationOutputIds.length === 0) {
          const errorMessage = isRunMode
            ? "No simulation outputs found for selected runs."
            : "No valid simulation outputs found."
          toast.danger({
            title: "Error",
            description: errorMessage,
          })
          setIsSaving(false)
          return
        }

        // Get existing simulation output IDs from the project
        const existingSimulationOutputs =
          currentProject.linked_simulation_ids || []

        // Filter out IDs that are already in the project
        const newSimulationOutputs = validSimulationOutputIds.filter(
          (id) => !existingSimulationOutputs.includes(id),
        )

        if (newSimulationOutputs.length === 0) {
          toast.success({
            title: "Already Added",
            description: `All selected ${selectedItemLabel} are already in this project.`,
          })
          setIsSaving(false)
          return
        }

        // Merge with existing simulation outputs
        const updatedSimulationOutputs = [
          ...existingSimulationOutputs,
          ...newSimulationOutputs,
        ]

        // Update the project
        await updateHumanReviewProject({
          id: projectId,
          simulationOutputs: updatedSimulationOutputs,
        })

        toast.success({
          title: "Added to project",
          description: `${capitalizedSelectedItemLabel} added to the Human Review project.`,
        })
        setIsOpen(false)
        setSelectedProjectId(undefined)
        setIsSaving(false)
        onSuccess?.()

        // Send email notification to all assignees automatically
        // Don't await - send in background
        // For bulk operations, send one email with the first simulation output ID
        // The email template will show all simulation outputs in the project
        sendProjectUpdate({
          projectId,
          isNewProject: false,
          simulationOutputId: newSimulationOutputs[0],
        }).catch((error) => {
          log.error("Failed to send email notifications", error)
          // Don't show error to user - email failure shouldn't block the workflow
        })
      } catch (error) {
        log.error("Error adding to human review project", error)
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred"
        toast.danger({
          title: "Error",
          description: `Failed to add to project: ${errorMessage}`,
        })
        setIsSaving(false)
      }
    },
    [
      selectedKeys,
      currentData,
      fetchHumanReviewProjectWithSimulationIds,
      updateHumanReviewProject,
      sendProjectUpdate,
      fetchSimulationOutputIdsForRun,
      itemType,
      onSuccess,
      capitalizedSelectedItemLabel,
      selectedItemLabel,
    ],
  )

  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      if (isSaving && !open) {
        return
      }

      setIsOpen(open)
      if (!open) {
        setSelectedProjectId(undefined)
      }
    },
    [isSaving],
  )

  return (
    <>
      <Button
        variant="secondary"
        disabled={isDisabled || isSaving || selectedCount === 0}
        onClick={() => setIsOpen(true)}
        data-testid="bulk-add-to-human-review-button"
      >
        <ClipboardCheck className="size-4" />
        Add to Project
      </Button>

      <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent
          size="md"
          data-testid="bulk-add-to-human-review-modal"
          onInteractOutside={(event) => {
            if (isSaving) {
              event.preventDefault()
            }
          }}
          onEscapeKeyDown={(event) => {
            if (isSaving) {
              event.preventDefault()
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              Add {selectedCount} {selectedItemLabel} to project
            </DialogTitle>
            <DialogDescription>
              Choose a Human Review project, then confirm to add the selected{" "}
              {selectedItemLabel}.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {isProjectsError && (
              <div
                className="flex items-center justify-between gap-3 pb-3"
                role="alert"
              >
                <p className="text-sm text-coval-danger-text">
                  Could not load Human Review projects.
                </p>
                <Button variant="secondary" onClick={onRetryProjects}>
                  Retry
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <div
                id={projectListLabelId}
                className="text-sm font-medium text-coval-text"
              >
                Pick a project
              </div>
              {humanReviewProjects.length > 0 ? (
                <RadioGroup
                  aria-labelledby={projectListLabelId}
                  value={selectedProjectId}
                  onValueChange={setSelectedProjectId}
                  className="max-h-72 overflow-y-auto pr-1"
                  disabled={isSaving}
                  data-testid="bulk-add-to-human-review-project-list"
                >
                  {humanReviewProjects.map((project) => {
                    const optionId = `human-review-project-${project.ulid}`
                    const isSelected = selectedProjectId === project.ulid

                    return (
                      <label
                        key={project.ulid}
                        htmlFor={optionId}
                        className={cn(
                          "flex cursor-pointer items-start gap-3 rounded-md border border-coval-border px-3 py-2.5 transition-colors hover:bg-surface-1",
                          isSelected &&
                            "border-coval-border-active bg-surface-1",
                          isSaving && "cursor-default opacity-50",
                        )}
                        data-project-name={project.name}
                        data-testid={`bulk-add-to-human-review-option-${project.ulid}`}
                      >
                        <RadioGroupItem
                          id={optionId}
                          value={project.ulid}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-coval-text">
                            {project.name}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </RadioGroup>
              ) : isProjectsError ? null : isProjectsLoading ? (
                <div
                  className="flex items-center justify-center gap-2 py-6"
                  aria-live="polite"
                >
                  <Loader className="size-4" />
                  <span>Loading Human Review projects...</span>
                </div>
              ) : (
                <div className="rounded-md border border-coval-border px-3 py-6 text-center text-sm text-coval-text-muted">
                  <p>No Human Review projects available.</p>
                  <Button asChild className="mt-3">
                    <Link
                      href={queryParams.getHref(
                        "/review/projects?create=true",
                        organizationSlug,
                      )}
                    >
                      Create project
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => handleModalOpenChange(false)}
              disabled={isSaving}
              data-testid="bulk-add-to-human-review-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedProjectId) {
                  void handleAddToProject(selectedProjectId)
                }
              }}
              disabled={!selectedProjectId || isSaving}
              data-testid="bulk-add-to-human-review-save-button"
            >
              {isSaving ? (
                <Loader className="size-4" />
              ) : (
                <Check className="size-4" />
              )}
              {isSaving ? "Adding..." : "Add to Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
